import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, errMsg, newIdemKey, type Card, type Charge, type InvoiceItem, type InvoiceView } from "../api";
import { CardBrand } from "../components/CardBrand";
import { ChargeForm } from "../components/ChargeForm";
import { HistoryModal } from "../components/HistoryModal";
import { Modal } from "../components/Modal";
import { RowMenu } from "../components/RowMenu";
import { SeriesMark, SituationLegend, SituationMark, type Sit } from "../components/Situation";
import { PayInvoiceDialog } from "../components/PayInvoiceDialog";
import { Btn, Field, Input, Notice, PageHead, Select } from "../components/ui";
import { useEntities } from "../entity";
import { fmtDay, formatMoney, todayISO } from "../lib/money";
import { usePeriod } from "../period";
import { useMe } from "../session";

const brl = (m: { decimal: string }) => formatMoney(m.decimal, "BRL");
const STATUS: Record<string, string> = { aberta: "Aberta", fechada: "Fechada", paga: "Paga" };
const todayStr = () => todayISO();
function sitOf(it: InvoiceItem, dueDate: string): Sit {
  if (it.type === "pagamento") return "realizado";
  if (it.confirmed || BigInt(it.paidCents?.cents ?? 0) >= BigInt(it.amount.cents)) return "realizado";
  return dueDate < todayStr() ? "vencido" : "agendado";
}
const monthLabel = (iso: string) => new Intl.DateTimeFormat("pt-BR", { month: "long", year: "numeric", timeZone: "UTC" }).format(new Date(iso + "T12:00:00Z"));

type CancelTarget = { kind: "charge"; item: InvoiceItem } | { kind: "payment"; item: InvoiceItem };

/** Fatura do cartão no estilo extrato: resumo, detalhamento, limite e lançamentos por dia. */
export function Cards() {
  const { me } = useMe();
  const realm = me.user.realm;
  const { current } = useEntities();
  const [sp, setSp] = useSearchParams();
  const [cards, setCards] = useState<Card[] | null>(null);
  const [view, setView] = useState<InvoiceView | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [dialog, setDialog] = useState<null | "charge" | "pay" | "close" | "reopen" | { edit: Charge }>(null);
  const [cancel, setCancel] = useState<CancelTarget | null>(null);
  const [hist, setHist] = useState<InvoiceItem | null>(null);
  const per = usePeriod();
  const cardId = sp.get("card");
  const dueParam = sp.get("due");
  // "Abrir fatura" de um lançamento traz o vencimento na URL: o mês do cabeçalho passa a ser o dele (uma única vez)
  useEffect(() => {
    if (!dueParam) return;
    per.goTo(dueParam);
    const n = new URLSearchParams(sp); n.delete("due"); setSp(n, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dueParam]);

  const loadCards = useCallback(async () => {
    if (!current) return;
    try {
      const l = await api<Card[]>("GET", `/api/${realm}/cards?entityId=${current.id}`);
      setCards(l);
    } catch (e) { setErr(errMsg(e)); }
  }, [realm, current]);
  useEffect(() => { void loadCards(); }, [loadCards]);

  const selected = useMemo(() => cards?.find((c) => c.id === cardId) ?? cards?.find((c) => c.status === "ativa") ?? cards?.[0] ?? null, [cards, cardId]);
  // a fatura exibida é a que VENCE no mês escolhido no cabeçalho (o mesmo seletor de mês das demais telas)
  const due = selected ? `${per.key}-${String(selected.dueDay).padStart(2, "0")}` : null;
  const loadInvoice = useCallback(async () => {
    if (!selected || !due) { setView(null); return; }
    try {
      const v = await api<InvoiceView>("GET", `/api/${realm}/cards/${selected.id}/invoice?due=${due}`);
      setView(v); setErr(null);
    } catch (e) { setErr(errMsg(e)); }
  }, [realm, selected, due]);
  useEffect(() => { void loadInvoice(); }, [loadInvoice]);

  const go = (next: { card?: string }) => {
    const n = new URLSearchParams(sp);
    if (next.card) n.set("card", next.card);
    setSp(n, { replace: true });
  };
  const refresh = async (msg?: string) => { setDialog(null); setCancel(null); if (msg) { setOk(msg); setErr(null); } await loadCards(); await loadInvoice(); };

  const act = async (path: string, body: object, msg: string) => {
    if (!selected) return;
    try { await api("POST", `/api/${realm}/${path}`, body, newIdemKey()); await refresh(msg); } catch (e) { setErr(errMsg(e)); }
  };

  const mark = async (it: InvoiceItem, what: "confirm" | "unconfirm" | "reconcile" | "unreconcile", msg: string) => {
    try { await api("POST", `/api/${realm}/card-charges/${it.id}/${({ confirm: "confirm", unconfirm: "unconfirm", reconcile: "mark-reconciled", unreconcile: "unmark-reconciled" } as const)[what]}`, { version: it.version }); await refresh(msg); } catch (e) { setErr(errMsg(e)); await loadInvoice(); }
  };

  if (!current || !cards) return <div className="page"><PageHead title="Cartões de Crédito" />{err ? <Notice kind="error">{err}</Notice> : <p className="muted">Carregando…</p>}</div>;
  if (cards.length === 0) {
    return (
      <div className="page">
        <PageHead title="Cartões de Crédito" sub="Faturas, limite e pagamentos." />
        <div className="empty-box"><p>Você ainda não cadastrou nenhum cartão de crédito.</p><Link className="btn primary" to={`/${realm}/contas-financeiras`} data-goto-accounts>Cadastrar em Contas Financeiras</Link></div>
      </div>
    );
  }
  const inv = view?.invoice;
  const card = view?.card ?? selected!;
  const editable = card.status === "ativa";

  return (
    <div className="page cards-page">
      <PageHead title="Cartões de Crédito" sub="A despesa entra nos relatórios quando a fatura é paga (regime de caixa)." />
      {ok && <Notice kind="ok">{ok}</Notice>}
      {err && <Notice kind="error">{err}</Notice>}
      <div className="card-bar">
        <CardBrand brand={card.brand} size={40} />
        <Select value={card.id} onChange={(e) => go({ card: e.target.value })} aria-label="Cartão" name="cardSelect">
          {cards.map((c) => <option key={c.id} value={c.id}>{c.name}{c.status === "inativa" ? " (inativo)" : ""}</option>)}
        </Select>
        <Link className="small" to={`/${realm}/contas-financeiras`}>Cadastro dos cartões em Contas Financeiras</Link>
      </div>

      {inv && view && (
        <div className="invoice-grid">
          <section className="invoice-side">
            <div className="invoice-nav">
              <button className="top-icon" aria-label="Fatura anterior" onClick={() => per.shift(-1)} data-inv-prev>‹</button>
              <div><b>{monthLabel(inv.dueDate).replace(/^./, (m) => m.toUpperCase())}</b><div className="small muted">Vencimento {fmtDay(inv.dueDate)}</div></div>
              <button className="top-icon" aria-label="Próxima fatura" onClick={() => per.shift(1)} data-inv-next>›</button>
            </div>
            <div className="inv-box" data-invoice-summary>
              <div className="inv-row"><span>Fechamento</span><span>{fmtDay(inv.closingDate)}</span></div>
              <div className="inv-row"><span>Vencimento</span><span>{fmtDay(inv.dueDate)}</span></div>
              <div className="inv-row"><span>Situação</span><b data-inv-status>{STATUS[inv.status]}</b></div>
              <hr />
              <div className="inv-row"><span>Saldo anterior</span><span>{brl(inv.carryIn)}</span></div>
              <div className="inv-row"><span>Compras do período</span><span>{brl(inv.charges)}</span></div>
              <div className="inv-row"><span>Total pago</span><span>{brl(inv.paid)}</span></div>
              <div className="inv-row"><span>Total</span><b>{brl(inv.total)}</b></div>
              <div className="inv-row big"><span>Valor a pagar</span><b data-inv-remaining>{brl(inv.remaining)}</b></div>
              {inv.settled && BigInt(inv.carriedOut.cents) > 0n && <p className="small muted">Quitada com saldo de {brl(inv.carriedOut)} que passou para a próxima fatura.</p>}
            </div>
            <div className="inv-box">
              <h4>Detalhamento</h4>
              <div className="inv-row"><span>Despesas</span><span>{brl(inv.singleCharges)}</span></div>
              <div className="inv-row"><span>Despesas fixas</span><span>{brl(inv.fixedCharges)}</span></div>
              <div className="inv-row"><span>Compras parceladas</span><span>{brl(inv.installmentCharges)}</span></div>
            </div>
            <div className="inv-box">
              <h4>Limite</h4>
              <div className="inv-row"><span>Limite do cartão</span><span>{brl(card.creditLimit)}</span></div>
              <div className="inv-row"><span>Utilizado</span><span>{brl(card.used)}</span></div>
              <div className="inv-row"><span>Disponível</span><b>{brl(card.available)}</b></div>
              <div className="inv-row small muted"><span>Paga pela conta</span><span>{card.payAccountName}</span></div>
            </div>
          </section>

          <section className="invoice-main">
            <div className="inv-actions">
              {editable && <Btn onClick={() => setDialog("charge")} data-new-charge>+ Nova compra</Btn>}
              {editable && (inv.closed
                ? <Btn variant="ghost" onClick={() => setDialog("reopen")} data-inv-reopen>Reabrir fatura</Btn>
                : !inv.settled && <Btn variant="ghost" onClick={() => setDialog("close")} data-inv-close>Fechar fatura</Btn>)}
              {!inv.settled && BigInt(inv.remaining.cents) > 0n && <Btn onClick={() => setDialog("pay")} data-inv-pay>Lançar pagamento</Btn>}
            </div>
            {view.items.length === 0 && <p className="muted">Nenhum lançamento nesta fatura.</p>}
            {view.items.length > 0 && (
              <div className="card table-card">
                <SituationLegend only={["realizado", "vencido", "agendado"]} noClip labels={{ realizado: "Confirmada / paga", vencido: "Vencida", agendado: "Em aberto" }} />
                <table aria-label="Lançamentos da fatura" data-invoice-table>
                  <thead><tr><th className="sit-col" aria-label="Situação" /><th>Data</th><th>Descrição</th><th>Categoria</th><th className="num">Valor</th><th>Conciliação</th><th aria-label="Ações" /></tr></thead>
                  <tbody>
                    {view.items.map((it) => {
                      const sit = sitOf(it, inv.dueDate);
                      return (
                        <tr key={it.type + it.id} data-inv-item data-item-type={it.type}>
                          <td className="sit-col"><SituationMark sit={sit} /></td>
                          <td className="tabular">{fmtDay(it.date)}</td>
                          <td>
                            <strong>{it.type === "pagamento" ? "Pagamento da fatura" : it.description}</strong>
                            {it.type === "compra" && <SeriesMark kind={it.seriesKind} no={it.installmentNo} total={it.installmentTotal} />}
                            {it.type === "compra" && it.counterpartyName && <div className="muted small">{it.counterpartyName}</div>}
                            {it.type === "pagamento" && <div className="muted small">Conta {it.payAccountName}{it.finalSettlement ? " · quitação final" : ""}</div>}
                          </td>
                          <td className="small">{it.type === "compra" ? it.categoryPath : "—"}</td>
                          <td className={`num tabular nowrap ${it.type === "pagamento" ? "pos" : "neg"}`}>{it.type === "pagamento" ? "+" : "−"} {brl(it.amount)}</td>
                          <td className="small" data-recon-cell>{it.type === "compra" ? (it.reconciled ? <span className="pill recon">Conciliado</span> : "—") : "—"}</td>
                          <td>
                            {it.type === "compra" && (
                              <RowMenu label={it.description}>
                                {editable && !it.confirmed && <button className="link" onClick={() => void mark(it, "confirm", "Compra confirmada.")} data-act="confirm">Confirmar</button>}
                                {editable && it.confirmed && !it.reconciled && <button className="link" onClick={() => void mark(it, "reconcile", "Compra conciliada.")} data-act="reconcile">Marcar como conciliado</button>}
                                {editable && it.reconciled && <button className="link" onClick={() => void mark(it, "unreconcile", "Conciliação desfeita.")} data-act="unreconcile">Desfazer conciliação</button>}
                                {editable && !inv.closed && !it.reconciled && <button className="link" onClick={() => void api<Charge>("GET", `/api/${realm}/card-charges/${it.id}`).then((c) => setDialog({ edit: c })).catch((e) => setErr(errMsg(e)))} data-act="edit">Editar</button>}
                                {editable && it.confirmed && !it.reconciled && <button className="link" onClick={() => void mark(it, "unconfirm", "Compra voltou a planejada.")} data-act="unconfirm">Voltar a planejado</button>}
                                {editable && !inv.closed && !it.reconciled && <button className="link danger" onClick={() => setCancel({ kind: "charge", item: it })} data-act="cancel">Excluir</button>}
                                <button className="link" onClick={() => setHist(it)} data-act="history">Histórico</button>
                              </RowMenu>
                            )}
                            {it.type === "pagamento" && editable && (
                              <RowMenu label="Pagamento da fatura"><button className="link danger" onClick={() => setCancel({ kind: "payment", item: it })} data-act="cancel">Excluir pagamento</button></RowMenu>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      )}

      {(dialog === "close" || dialog === "reopen") && inv && (
        <InvoiceLockDialog mode={dialog} card={card} invoice={inv} onClose={() => setDialog(null)}
          onConfirm={() => act(`cards/${card.id}/invoice/${dialog === "close" ? "close" : "reopen"}`, { dueDate: inv.dueDate }, dialog === "close" ? "Fatura fechada." : "Fatura reaberta.")} />
      )}
      {dialog === "charge" && <ChargeForm card={card} charge={null} onClose={() => setDialog(null)} onDone={(m) => void refresh(m)} />}
      {typeof dialog === "object" && dialog && "edit" in dialog && <ChargeForm card={card} charge={dialog.edit} onClose={() => setDialog(null)} onDone={(m) => void refresh(m)} />}
      {hist && <HistoryModal title={`Histórico — ${hist.description}`} path={`/api/${realm}/card-charges/${hist.id}/history`} onClose={() => setHist(null)} />}
      {dialog === "pay" && inv && <PayInvoiceDialog card={card} invoice={inv} onClose={() => setDialog(null)} onDone={(m) => void refresh(m)} />}
      {cancel && <CancelDialog target={cancel} onClose={() => setCancel(null)} onConfirm={async (reason, scope) => {
        try {
          if (cancel.kind === "payment") await api("POST", `/api/${realm}/card-payments/${cancel.item.id}/cancel`, { reason }, newIdemKey());
          else await api("POST", `/api/${realm}/card-charges/${cancel.item.id}/cancel`, { version: cancel.item.version, reason, scope }, newIdemKey());
          await refresh(cancel.kind === "payment" ? "Pagamento excluído." : "Compra excluída.");
        } catch (e) { setErr(errMsg(e)); setCancel(null); }
      }} />}
    </div>
  );
}

/** Confirmação de fechar/reabrir a fatura: mostra valores e datas antes de qualquer alteração. */
function InvoiceLockDialog({ mode, card, invoice, onClose, onConfirm }: { mode: "close" | "reopen"; card: Card; invoice: InvoiceView["invoice"]; onClose: () => void; onConfirm: () => Promise<void> }) {
  const [busy, setBusy] = useState(false);
  const closing = mode === "close";
  return (
    <Modal title={closing ? "Fechar fatura" : "Reabrir fatura"} onClose={onClose}>
      <div data-lock-dialog={mode}>
        <div className="inv-box" style={{ marginTop: 0 }}>
          <div className="inv-row"><span>Cartão</span><b>{card.name}</b></div>
          <div className="inv-row"><span>Fatura com vencimento em</span><span>{fmtDay(invoice.dueDate)}</span></div>
          <div className="inv-row"><span>Data de fechamento do ciclo</span><span>{fmtDay(invoice.closingDate)}</span></div>
          <div className="inv-row"><span>Total da fatura</span><b data-lock-total>{brl(invoice.total)}</b></div>
          <div className="inv-row"><span>Valor a pagar</span><b>{brl(invoice.remaining)}</b></div>
        </div>
        {closing
          ? <p className="small muted">Com a fatura fechada, não entram novas compras nem alterações nela. O pagamento continua liberado. A despesa só passa a paga quando você lançar o pagamento da fatura; até lá ela segue como planejada em Contas a Pagar e Receber (marcada como fechada). Você pode reabrir enquanto ela não for quitada.</p>
          : <p className="small muted">Reabrir libera novas compras e alterações nesta fatura. O valor a pagar volta a acompanhar as compras lançadas.</p>}
        <div className="modal-actions">
          <Btn variant="ghost" onClick={onClose}>Voltar</Btn>
          <button type="button" className="btn primary" disabled={busy} data-lock-confirm onClick={() => { setBusy(true); void onConfirm().finally(() => setBusy(false)); }}>{busy ? "Aguarde…" : closing ? "Confirmar fechamento" : "Confirmar reabertura"}</button>
        </div>
      </div>
    </Modal>
  );
}

function CancelDialog({ target, onClose, onConfirm }: { target: CancelTarget; onClose: () => void; onConfirm: (reason: string, scope: "this" | "following") => Promise<void> }) {
  const [reason, setReason] = useState("");
  const [scope, setScope] = useState<"this" | "following">("this");
  const [busy, setBusy] = useState(false);
  const inSeries = target.kind === "charge" && !!target.item.seriesKind;
  return (
    <Modal title={target.kind === "payment" ? "Excluir pagamento" : "Excluir compra"} onClose={onClose}>
      <form onSubmit={(e) => { e.preventDefault(); if (reason.trim().length < 3) return; setBusy(true); void onConfirm(reason.trim(), scope).finally(() => setBusy(false)); }}>
        {inSeries && (
          <div className="scope-choices">
            <label><input type="radio" checked={scope === "this"} onChange={() => setScope("this")} /> Somente esta</label>
            <label><input type="radio" checked={scope === "following"} onChange={() => setScope("following")} /> Esta e as seguintes</label>
          </div>
        )}
        <Field label="Motivo da exclusão (mínimo 3 letras)" required><Input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={300} name="reason" /></Field>
        <div className="modal-actions"><Btn variant="ghost" onClick={onClose}>Voltar</Btn><button type="submit" className="btn danger" disabled={busy || reason.trim().length < 3} data-cancel-confirm>Confirmar exclusão</button></div>
      </form>
    </Modal>
  );
}
